import { App, Flex } from "antd";
import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiError } from "tm-shared/api";
import { Point } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { PointInfo } from "@/domains/point";
import { useAddUnscheduledVisits } from "@/domains/point";
import { BaseModal } from "@/shared/ui";
import BaseButton from "@/shared/ui/BaseButton";

import { StyledPointCard, StyledPointList } from "./Styled";

interface AddPointConfirmModalProps {
    open: boolean;
    pointExtraIds: string[];
    selectedPoints: Point[];
    onClose: () => void;
}

export const AddPointConfirmModal: FC<AddPointConfirmModalProps> = ({
    open,
    pointExtraIds,
    selectedPoints,
    onClose,
}) => {
    const { notification } = App.useApp();

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const availablePoints = selectedPoints.filter(
        (point) => !pointExtraIds?.includes(point.id),
    );

    const { addUnscheduledVisits } = useAddUnscheduledVisits();

    const handleAddPoints = useCallback(async () => {
        setLoading(true);
        try {
            const pointIds = availablePoints.map((point) => point.id);

            await addUnscheduledVisits({ pointIds });

            navigate(ROUTES.WorkPlan);

            onClose();
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Помилка при додаванні магазинів",
                description: displayMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [
        notification,
        availablePoints,
        addUnscheduledVisits,
        navigate,
        onClose,
    ]);

    if (availablePoints.length === 0) return null;

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            actions={null}
            title={`Буде додано ${availablePoints.length} торгових точок`}
            titleStyle={{ fontSize: 16 }}
        >
            <StyledPointList>
                {availablePoints.map((point) => (
                    <StyledPointCard key={point.id}>
                        <PointInfo point={point} />
                    </StyledPointCard>
                ))}
            </StyledPointList>

            <Flex vertical gap={12}>
                <BaseButton
                    bgColor="success"
                    type="primary"
                    disabled={loading}
                    loading={loading}
                    onClick={handleAddPoints}
                >
                    Додати всі
                </BaseButton>

                <BaseButton type="default" onClick={onClose}>
                    Скасувати
                </BaseButton>
            </Flex>
        </BaseModal>
    );
};
